import DashboardLayout from "@/pages/Dashboard/Layout/DashboardLayout.vue";
import AuthLayout from "@/pages/Auth/layouts/AuthLayout.vue";

// Guards
import isAuthenticatedGuard from "@/pages/Auth/guards/is-authenticated.guard";
import isAdminGuard from "@/pages/Auth/guards/is-admin.guard";
import isAdminRoleGuard from "@/pages/Auth/guards/is-admin-role.guard";
import translationModuleGuard from "@/pages/Auth/guards/translation-module.guard";
import hasEditPermissionGuard from "@/pages/Auth/guards/has-edit-permission.guard";
import canAccessUsersSettingsGuard from "@/pages/Auth/guards/can-access-users-settings.guard";
import canViewUserProfileGuard from "@/pages/Auth/guards/can-view-user-profile.guard";
import dynamicModelPrettyUrlGuard from "@/pages/Auth/guards/dynamic-model-pretty-url.guard";

// Auth pages
import { authPages } from "@/pages/Auth/routes/index.js";

// Page module
import { pageModuleRoutes } from "@/modules/page_module";

// Instance Builder module
import { instanceBuilderRoutes } from "@/modules/instance_builder_module";
import { cronjobsModuleRoutes } from "@/modules/cronjobs_module";
import { comexstatsModuleRoutes } from "@/modules/comexstats_module";
import { historyModuleRoutes } from "@/modules/history_module";
import { fobtradingModuleRoutes } from "@/modules/fobtrading_module";
import { mevModuleRoutes } from "@/modules/mev_module";
import { outlookModuleRoutes } from "@/modules/outlook_module";
import { jurisprudenciaModuleRoutes } from "@/modules/jurisprudencia_module";
import { arcaModuleRoutes } from "@/modules/arca_module";
import { telegramAiStandaloneRoutes, telegramAiModuleRoutes } from "@/modules/telegram_ai_module";
import { inmobiliariaModuleRoutes } from "@/modules/inmobiliaria_module";
import { facturacionAbogadosModuleRoutes } from "@/modules/facturacion_abogados_module";
import { notaComercialModuleRoutes } from "@/modules/nota_comercial_module";
import { bolsaComercialesModuleRoutes } from "@/modules/bolsa_comerciales_module";
import { alisofModuleRoutes } from "@/modules/alisof_module";

// Dashboard pages
import AdminDashboard from "@/pages/Dashboard/AdminDashboard.vue";
import Dashboard from "@/pages/Dashboard/Dashboard.vue";

import Settings from "@/pages/Auth/Settings.vue";
import { billingModuleRoutes } from "@/modules/billing_module";
import Orphans from "@/pages/Settings/Orphans.vue";
import MyProfile from "@/pages/MyProfile.vue";

import Tutorials from "@/pages/Tutorials.vue";
import Developpers from "@/pages/Developpers.vue";

import Users from "@/pages/gotrading/users.vue";
import Roles from "@/pages/gotrading/roles.vue";
// Mock component to replace non-existent import
const MyTeam = {
  template: "<div>MyTeam component not available</div>",
  name: "MyTeam",
};

import Categories from "@/pages/Categories/list.vue";

// Pages
import User from "@/pages/Dashboard/Pages/UserProfile.vue";
import Pricing from "@/pages/Dashboard/Pages/Pricing.vue";
import Subscribe from "@/pages/Dashboard/Pages/Subscribe.vue";
import TimeLine from "@/pages/Dashboard/Pages/TimeLinePage.vue";
import RtlSupport from "@/pages/Dashboard/Pages/RtlSupport.vue";
import Login from "@/pages/Dashboard/Pages/Login.vue";
import Lock from "@/pages/Dashboard/Pages/Lock.vue";

import MercadoPagoOauth from "@/pages/Dashboard/Components/MercadoPagoOauth.vue";

// Components pages
import Buttons from "@/pages/Dashboard/Components/Buttons.vue";
import GridSystem from "@/pages/Dashboard/Components/GridSystem.vue";
import Panels from "@/pages/Dashboard/Components/Panels.vue";
import SweetAlert from "@/pages/Dashboard/Components/SweetAlert.vue";
import Notifications from "@/pages/Dashboard/Components/Notifications.vue";
import Icons from "@/pages/Dashboard/Components/Icons.vue";
import Typography from "@/pages/Dashboard/Components/Typography.vue";

// Forms pages
import RegularForms from "@/pages/Dashboard/Forms/RegularForms.vue";
import ExtendedForms from "@/pages/Dashboard/Forms/ExtendedForms.vue";
import ValidationForms from "@/pages/Dashboard/Forms/ValidationForms.vue";
import Wizard from "@/pages/Dashboard/Forms/Wizard.vue";

// TableList pages
import RegularTables from "@/pages/Dashboard/Tables/RegularTables.vue";
import ExtendedTables from "@/pages/Dashboard/Tables/ExtendedTables.vue";
import PaginatedTables from "@/pages/Dashboard/Tables/PaginatedTables.vue";

// Maps pages
import GoogleMaps from "@/pages/Dashboard/Maps/GoogleMaps.vue";
import FullScreenMap from "@/pages/Dashboard/Maps/FullScreenMap.vue";
import VectorMaps from "@/pages/Dashboard/Maps/VectorMaps.vue";

// Calendar
import Calendar from "@/pages/Dashboard/Calendar.vue";
// Charts
import Charts from "@/pages/Dashboard/Charts.vue";
import Widgets from "@/pages/Dashboard/Widgets.vue";

import NotFound from "../pages/NotFound.vue"; // Pantalla para "No hay nada aquí"

import Classes from "@/pages/Classes.vue";
import MyClasses from "@/pages/MyClasses.vue";

import { i18n } from "../main"; // Asegúrate de que esta ruta es correcta

import ProductsPage from "@/pages/gotrading/products.vue";
import ContactsPage from "@/pages/gotrading/contacts.vue";
import ProformsPage from "@/pages/gotrading/proforms.vue";
import AddressesPage from "@/pages/gotrading/addresses.vue";
import BankAccountsPage from "@/pages/gotrading/bankaccounts.vue";
import FreightLinesPage from "@/pages/gotrading/freightlines.vue";
import ListsPage from "@/pages/gotrading/lists.vue";
import ListEditPage from "@/pages/gotrading/list-edit.vue";
import FieldListMappingsPage from "@/pages/gotrading/fieldListMappings.vue";
import DragTest from "@/pages/gotrading/drag.vue";
import DynamicModel from "@/pages/gotrading/dynamic-model.vue";
import DynamicModelDetail from "@/pages/gotrading/dynamic-model-detail.vue";
import DynamicModelEdit from "@/pages/gotrading/dynamic-model-edit.vue";
import { hasInlineProfileEdit, prefersClassicView } from "@/components/gotrading/item-profile/profileLayouts";
import DynamicModelDisplay from "@/pages/gotrading/dynamic-model-display.vue";
import GoTradingDashboard from "@/pages/gotrading/Dashboard.vue";
import ModelReferenceMapping from "@/pages/gotrading/model-reference-mapping.vue";
import AdminLinks from "@/pages/gotrading/AdminLinks.vue";
import RefIntegrityAdmin from "@/pages/gotrading/RefIntegrityAdmin.vue";
// dynamic-model-add.vue se mantiene para la versión antigua con Form.vue; la ruta "add" usa DynamicModelEdit
import UserView from "@/pages/gotrading/user-view.vue";
import PromptSettings from "@/pages/Auth/PromptSettings.vue";
import AIModelContext from "@/pages/gotrading/ai-model-context.vue";

let componentsMenu = {
  path: "/components",
  component: DashboardLayout,
  redirect: "/components/buttons",
  name: "Components",
  beforeEnter: isAuthenticatedGuard,
  children: [
    {
      path: "buttons",
      name: "Buttons",
      components: { default: Buttons },
    },
    {
      path: "grid-system",
      name: "Grid System",
      components: { default: GridSystem },
    },
    {
      path: "panels",
      name: "Panels",
      components: { default: Panels },
    },
    {
      path: "sweet-alert",
      name: "Sweet Alert",
      components: { default: SweetAlert },
    },
    {
      path: "icons",
      name: "Icons",
      components: { default: Icons },
    },
    {
      path: "typography",
      name: "Typography",
      components: { default: Typography },
    },
  ],
};
let formsMenu = {
  path: "/forms",
  component: DashboardLayout,
  redirect: "/forms/regular",
  name: "Forms",
  beforeEnter: isAuthenticatedGuard,
  children: [
    {
      path: "regular",
      name: "Regular Forms",
      components: { default: RegularForms },
    },
    {
      path: "extended",
      name: "Extended Forms",
      components: { default: ExtendedForms },
    },
    {
      path: "validation",
      name: "Validation Forms",
      components: { default: ValidationForms },
    },
    {
      path: "wizard",
      name: "Wizard",
      components: { default: Wizard },
    },
  ],
};

let tablesMenu = {
  path: "/table-list",
  component: DashboardLayout,
  redirect: "/table-list/regular",
  name: "Tables",
  beforeEnter: isAuthenticatedGuard,
  children: [
    {
      path: "regular",
      name: "Regular Tables",
      components: { default: RegularTables },
    },
    {
      path: "extended",
      name: "Extended Tables",
      components: { default: ExtendedTables },
    },
    {
      path: "paginated",
      name: "Pagianted Tables",
      components: { default: PaginatedTables },
    },
  ],
};

let mapsMenu = {
  path: "/maps",
  component: DashboardLayout,
  name: "Maps",
  beforeEnter: isAuthenticatedGuard,
  redirect: "/maps/google",
  children: [
    {
      path: "google",
      name: "Google Maps",
      components: { default: GoogleMaps },
    },
    {
      path: "full-screen",
      name: "Full Screen Map",
      meta: {
        hideContent: true,
        hideFooter: true,
        navbarAbsolute: true,
      },
      components: { default: FullScreenMap },
    },
    {
      path: "vector-map",
      name: "Vector Map",
      components: { default: VectorMaps },
    },
  ],
};

let pagesMenu = {
  path: "/pages",
  component: DashboardLayout,
  name: "Pages",
  beforeEnter: isAuthenticatedGuard,
  redirect: "/pages/user",
  children: [
    {
      path: "user",
      name: "User Page",
      components: { default: User },
    },
    {
      path: "timeline",
      name: "Timeline Page",
      components: { default: TimeLine },
    },
  ],
};

const routes = [
  {
    path: "/login",
    component: AuthLayout,
    children: [
      {
        path: "",
        name: "Login",
        component: Login,
      },
    ],
  },
  authPages,
  {
    path: "/gotrading",
    component: DashboardLayout,
    beforeEnter: isAuthenticatedGuard,
    children: [
      {
        path: "",
        redirect: "/gotrading/dashboard",
      },
      {
        path: "dashboard",
        name: "GoTradingDashboard",
        component: GoTradingDashboard,
        meta: { title: "Dashboard" },
      },
      {
        path: "prompt-settings",
        name: "Prompt Settings",
        component: PromptSettings,
        beforeEnter: isAuthenticatedGuard,
      },
      {
        path: "ai-model-context",
        name: "AI Model Context",
        component: AIModelContext,
        beforeEnter: isAuthenticatedGuard,
      },
      {
        path: "user-menu",
        name: "UserMenuSettings",
        component: () => import("@/pages/gotrading/UserMenuSettings.vue"),
        beforeEnter: isAuthenticatedGuard,
        meta: { title: "Mi menú" },
      },
    ],
  },
  {
    path: "/settings",
    component: DashboardLayout,
    beforeEnter: isAuthenticatedGuard,
    children: [
      {
        path: "",
        name: "Settings",
        components: { default: Settings },
        beforeEnter: isAdminGuard,
      },
      {
        path: "telegram",
        redirect: { name: "Settings", query: { tab: "telegram" } },
      },
      {
        path: "orphans",
        name: "Orphans",
        components: { default: Orphans },
        beforeEnter: isAdminRoleGuard,
      },
      {
        path: "migrations",
        name: "Migrations",
        components: {
          default: () => import("@/pages/Settings/Migrations.vue"),
        },
        beforeEnter: isAdminRoleGuard,
        meta: { title: "Migraciones" },
      },
    ],
  },
  {
    // Instructivos del tenant: /tutorials/menu.html. Sin layout, para exportarlos a PDF.
    path: "/tutorials/:slug",
    name: "TenantTutorial",
    component: () => import("@/pages/gotrading/TenantTutorial.vue"),
    beforeEnter: isAuthenticatedGuard,
    meta: { title: "Tutorial" },
  },
  ...telegramAiStandaloneRoutes,
  {
    path: "/tracking/:bookingNumber",
    name: "Tracking",
    component: () => import("@/pages/gotrading/tracking.vue"),
    meta: { title: "Tracking Information" },
  },
  {
    path: "/file/:token",
    name: "SharedFile",
    component: () => import("@/pages/SharedFileView.vue"),
    meta: { public: true, title: "Archivo compartido" },
  },
  {
    path: "/",
    component: DashboardLayout,
    beforeEnter: isAuthenticatedGuard,
    children: [
      {
        path: "",
        redirect: "/dashboard",
      },
      {
        path: "dashboard",
        name: "Dashboard",
        component: GoTradingDashboard,
        meta: { title: "Dashboard" },
      },
      ...billingModuleRoutes,
      ...telegramAiModuleRoutes,
      componentsMenu,
      formsMenu,
      tablesMenu,
      mapsMenu,
      pagesMenu,

      {
        path: "modules",
        name: "SystemModules",
        components: { default: () => import("@/pages/SystemModules.vue") },
        meta: { title: "Módulos del Sistema", requiresAuth: true },
        beforeEnter: isAdminRoleGuard,
      },
      {
        path: "admin/ref-integrity",
        name: "RefIntegrityAdmin",
        components: { default: RefIntegrityAdmin },
        meta: { title: "Applied-to reference integrity", requiresAuth: true },
        beforeEnter: isAdminRoleGuard,
      },
      {
        path: "json-import",
        name: "JSONImport",
        components: { default: () => import("@/pages/gotrading/json-import.vue") },
        meta: { title: "Importación JSON", requiresAuth: true },
        beforeEnter: isAdminGuard,
      },
      {
        path: "financial",
        name: "FinancialModule",
        components: { default: () => import("@/pages/FinancialModule.vue") },
        meta: { title: "Módulo Financiero", requiresAuth: true, requiresModule: "financial_module" },
      },
      {
        path: "financial/payments/view/:id",
        name: "FinancialPaymentView",
        components: { default: () => import("@/pages/financial/FinancialPaymentView.vue") },
        meta: { title: "Pago", requiresAuth: true, requiresModule: "financial_module" },
      },
      {
        path: "financial/payments",
        name: "FinancialPayments",
        components: { default: () => import("@/pages/financial/Payments.vue") },
        meta: { title: "Pagos", requiresAuth: true, requiresModule: "financial_module" },
      },
      {
        path: "financial/expenses",
        name: "FinancialExpenses",
        components: { default: () => import("@/pages/financial/Expenses.vue") },
        meta: { title: "Gastos", requiresAuth: true, requiresModule: "financial_module" },
      },
      {
        path: "financial/collects",
        name: "FinancialCollects",
        components: { default: () => import("@/pages/financial/Collects.vue") },
        meta: { title: "Cobros", requiresAuth: true, requiresModule: "financial_module" },
      },
      {
        path: "stock",
        name: "StockModule",
        components: { default: () => import("@/pages/StockModule.vue") },
        meta: { title: "Gestión de Stock", requiresAuth: true, requiresModule: "stock_module" },
      },
      {
        path: "containers",
        name: "ContainersModule",
        components: { default: () => import("@/pages/StockModule.vue") },
        meta: { title: "Contenedores", requiresAuth: true, requiresModule: "stock_module" },
      },
      {
        path: "chat",
        name: "ChatHistory",
        components: { default: () => import("@/modules/chat_module/pages/ChatHistory.vue") },
        meta: { title: "Chat", requiresAuth: true, requiresModule: "chat_module" },
      },
      {
        path: "financial/account-statement",
        name: "FinancialAccountStatement",
        components: {
          // Una fila por contacto; al abrirla, el mismo extracto de la ficha
          // del contacto (ticket #437).
          default: () => import("@/pages/financial/AccountStatementByContact.vue"),
        },
        meta: { title: "Cuenta Corriente", requiresAuth: true, requiresModule: "financial_module" },
      },
      {
        path: "accounting",
        name: "AccountingModule",
        components: { default: () => import("@/pages/gotrading/accounting-entries.vue") },
        meta: { title: "Accounting", requiresAuth: true, requiresModule: "accounting_module" },
      },
      {
        path: "dhl/tracking",
        name: "DHLTracking",
        components: {
          default: () => import("@/pages/dhl/Tracking.vue"),
        },
        meta: { title: "DHL Tracking", requiresAuth: true, requiresModule: "dhl_module" },
      },
      {
        path: "ft/container-tracking",
        name: "ContainerTracking",
        components: {
          default: () => import("@/pages/findTeu/ContainerTracking.vue"),
        },
        meta: { title: "Container Tracking", requiresAuth: true, requiresModule: "findTeu_module" },
      },
      {
        path: "excel-mapping",
        name: "ExcelMapping",
        components: {
          default: () => import("@/pages/excelMapping/ExcelMapping.vue"),
        },
        meta: { title: "Excel Mapping", requiresAuth: true, requiresModule: "excel_mapping_module" },
      },
      {
        path: "support/tickets",
        name: "SupportTicketsList",
        components: {
          default: () => import("@/pages/support/SupportTicketsList.vue"),
        },
        meta: { title: "Support Tickets", requiresAuth: true, requiresModule: "support_module" },
      },
      {
        path: "support/tickets/:id",
        name: "SupportTicketDetail",
        components: {
          default: () => import("@/pages/support/SupportTicketDetail.vue"),
        },
        meta: { title: "Support Ticket", requiresAuth: true, requiresModule: "support_module" },
      },
      {
        path: "support/create",
        name: "CreateSupportTicket",
        components: {
          default: () => import("@/pages/support/CreateSupportTicket.vue"),
        },
        meta: { title: "Create Support Ticket", requiresAuth: true, requiresModule: "support_module" },
      },
      {
        path: "legal-conditions",
        name: "LegalConditions",
        components: {
          default: () => import("@/pages/LegalConditions.vue"),
        },
        meta: { title: "Terms of Service", requiresAuth: false },
      },
      {
        path: "faq",
        name: "Faq",
        components: {
          default: () => import("@/pages/Faq.vue"),
        },
        meta: { title: "FAQ", requiresAuth: false },
      },
      {
        path: "websites",
        name: "Websites",
        components: {
          default: () => import("@/pages/websites/Websites.vue"),
        },
        meta: { title: "Websites", requiresAuth: true, requiresModule: "website_module" },
      },
      {
        path: "emails",
        name: "Emails",
        components: {
          default: () => import("@/pages/emails/Emails.vue"),
        },
        meta: { title: "Emails", requiresAuth: true, requiresModule: "email_module" },
      },
      {
        path: "translations",
        name: "Translations",
        components: {
          default: () => import("@/pages/translations/Translations.vue"),
        },
        meta: { title: "Traducciones", requiresAuth: true },
        beforeEnter: translationModuleGuard,
      },
      {
        path: "websites/:id",
        name: "WebsiteDetail",
        components: {
          default: () => import("@/pages/websites/WebsiteDetail.vue"),
        },
        meta: { title: "Website Detail", requiresAuth: true, requiresModule: "website_module" },
      },
      {
        path: "websites/:websiteId/pages/:pageId",
        name: "PageEdit",
        components: {
          default: () => import("@/pages/websites/PageEdit.vue"),
        },
        meta: { title: "Edit Page", requiresAuth: true, requiresModule: "website_module" },
      },
      {
        path: "websites/:websiteId/blocks/:blockId",
        name: "BlockEdit",
        components: {
          default: () => import("@/pages/websites/PageEdit.vue"),
        },
        props: { default: { mode: 'block' } },
        meta: { title: "Edit Block", requiresAuth: true, requiresModule: "website_module" },
      },
      {
        path: "relationships",
        name: "Relationships",
        components: {
          default: () => import("@/pages/gotrading/Relationships.vue"),
        },
        meta: { title: "Relaciones", requiresAuth: true },
      },
      {
        path: "myprofile",
        name: "MyProfile",
        components: { default: MyProfile },
      },
      {
        path: "myclasses",
        name: "MyClasses",
        components: { default: MyClasses },
      },
      {
        path: "classes",
        name: "Classes",
        components: { default: Classes },
      },
      {
        path: "users",
        name: "Users",
        components: { default: Users },
        beforeEnter: canAccessUsersSettingsGuard,
      },
      {
        path: "developpers",
        name: "Developpers",
        components: { default: Developpers },
      },
      {
        path: "admin",
        name: "AdminDashboard",
        components: { default: AdminDashboard },
      },
      {
        path: "notifications",
        name: "Notifications",
        components: { default: Notifications },
      },
      {
        path: "charts",
        name: "Charts",
        components: { default: Charts },
      },
      {
        path: "widgets",
        name: "Widgets",
        components: { default: Widgets },
      },
      {
        path: "lists",
        name: "ListsPage",
        components: { default: ListsPage },
      },
      {
        path: "lists/:id/edit",
        name: "ListEditPage",
        components: { default: ListEditPage },
        beforeEnter: isAuthenticatedGuard,
        meta: { title: "Editar listado" },
      },
      {
        path: "field-list-mappings",
        name: "FieldListMappingsPage",
        components: { default: FieldListMappingsPage },
      },
      {
        path: "model-definitions",
        name: "ModelDefinitionsPage",
        components: {
          default: () => import("@/pages/gotrading/model-definitions.vue"),
        },
      },
      {
        path: "query-reports",
        name: "QueryReports",
        component: () => import("@/pages/gotrading/QueryReports.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "hook-automations",
        name: "HookAutomations",
        component: () => import("@/pages/gotrading/HookAutomations.vue"),
        beforeEnter: isAdminRoleGuard,
        meta: { requiresAuth: true },
      },
      {
        path: "accounting",
        name: "AccountingEntries",
        component: () => import("@/pages/gotrading/accounting-entries.vue"),
        meta: { requiresAuth: true, title: "Asientos Contables" },
      },
      {
        path: "dynamic-model/:modelName/trash",
        name: "DynamicModelTrash",
        component: DynamicModel,
      },
      {
        path: "dynamic-model/:modelName/archive",
        name: "DynamicModelArchive",
        component: DynamicModel,
      },
      {
        path: "dynamic-model/:modelName",
        name: "DynamicModel",
        component: DynamicModel,
      },
      {
        path: "dynamic-model/:modelName/add",
        name: "DynamicModelAdd",
        component: DynamicModelEdit,
        beforeEnter: async (to, from, next) => {
          // Primero verificar autenticación
          if (!localStorage.getItem("token")) {
            return next("/login");
          }
          // Las notas comerciales se dan de alta desde el simulador, nunca con el formulario común
          if (String(to.params.modelName || "").toLowerCase() === "notacomercial") {
            return next("/nota-comercial/simulador");
          }
          // Luego verificar permisos de edición (create requiere manage)
          await hasEditPermissionGuard(to, from, next);
        },
      },
      {
        path: "view/:viewId",
        name: "DynamicModelReport",
        component: DynamicModel,
        beforeEnter: isAuthenticatedGuard,
      },
      {
        // Ruta específica para ver perfil de usuario
        path: "dynamic-model/users/view/:itemId",
        name: "UserView",
        component: UserView,
        beforeEnter: canViewUserProfileGuard,
      },
      {
        path: "i/:itemSlug",
        name: "DynamicModelDetailPretty",
        component: DynamicModelDetail,
        beforeEnter: dynamicModelPrettyUrlGuard,
      },
      {
        path: "dynamic-model/:modelName/view/:itemId",
        name: "DynamicModelDetail",
        component: DynamicModelDetail,
        beforeEnter: isAuthenticatedGuard,
      },
      {
        path: "dynamic-model/:modelName/edit/:itemId",
        name: "DynamicModelEdit",
        component: DynamicModelEdit,
        beforeEnter: async (to, from, next) => {
          // Primero verificar autenticación
          if (!localStorage.getItem("token")) {
            return next("/login");
          }
          // Los modelos con ficha se editan en línea; Form2 solo desde «Formulario completo».
          // Con «Vista anterior» elegida, siempre el formulario.
          const toItemProfile =
            hasInlineProfileEdit(to.params.modelName) && !to.query.fullForm && !prefersClassicView();
          // Luego verificar permisos de edición
          await hasEditPermissionGuard(to, from, (arg) => {
            if (arg === undefined && toItemProfile) {
              return next({
                name: "DynamicModelDetail",
                params: { modelName: to.params.modelName, itemId: to.params.itemId },
                query: { edit: "1" },
              });
            }
            return next(arg);
          });
        },
      },
      {
        path: "dynamic-model/:modelName/edit/:itemId/:viewId",
        name: "DynamicModelEditWithView",
        component: DynamicModelEdit,
        beforeEnter: async (to, from, next) => {
          // Primero verificar autenticación
          if (!localStorage.getItem("token")) {
            return next("/login");
          }
          // Luego verificar permisos de edición
          await hasEditPermissionGuard(to, from, next);
        },
      },
      {
        path: "dynamic-model/:modelName/display/:itemId/:viewId",
        name: "DynamicModelContentView",
        component: DynamicModelDisplay,
        beforeEnter: isAuthenticatedGuard,
      },
      // Ruta unificada para todos los templates de documentos
      {
        path: "templates/:type/:id",
        name: "DocumentTemplate",
        component: () => import("@/pages/gotrading/DocumentTemplate.vue"),
        beforeEnter: isAuthenticatedGuard,
        meta: { title: "Document View" },
      },
      // Page module templates (V2)
      ...pageModuleRoutes,
      // Instance Builder module
      ...instanceBuilderRoutes,
      {
        path: "agent-ai",
        redirect: { name: "Settings", query: { tab: "telegram" } },
      },
      {
        path: "telegram-ai",
        redirect: { name: "Settings", query: { tab: "telegram" } },
      },
      // CRONJOBS module
      ...cronjobsModuleRoutes,
      ...comexstatsModuleRoutes,
      ...historyModuleRoutes,
      ...fobtradingModuleRoutes,
      ...mevModuleRoutes,
      ...outlookModuleRoutes,
      ...jurisprudenciaModuleRoutes,
      ...arcaModuleRoutes,
      ...inmobiliariaModuleRoutes,
      ...facturacionAbogadosModuleRoutes,
      ...notaComercialModuleRoutes,
      ...bolsaComercialesModuleRoutes,
      ...alisofModuleRoutes,
      // Rutas legacy (redirigen al componente unificado)
      {
        path: "templates/proforma/:id",
        name: "ProformaTemplate",
        redirect: to => ({ name: "DocumentTemplate", params: { type: "proforma", id: to.params.id } }),
      },
      {
        path: "templates/invoice/:id",
        name: "InvoiceTemplate",
        redirect: to => ({ name: "DocumentTemplate", params: { type: "invoice", id: to.params.id } }),
      },
      {
        path: "templates/packing-list/:id",
        name: "PackingListTemplate",
        redirect: to => ({ name: "DocumentTemplate", params: { type: "packing-list", id: to.params.id } }),
      },
      {
        path: "templates/instructions/:id",
        name: "InstructionsTemplate",
        redirect: to => ({ name: "DocumentTemplate", params: { type: "instructions", id: to.params.id } }),
      },
      {
        path: "templates/proform-supplier/:id",
        name: "ProformSupplierTemplate",
        redirect: to => ({ name: "DocumentTemplate", params: { type: "proform-supplier", id: to.params.id } }),
      },
      {
        path: "templates/nota-comercial/:id",
        name: "NotaComercialTemplate",
        redirect: to => ({ name: "DocumentTemplate", params: { type: "nota-comercial", id: to.params.id } }),
      },
      {
        path: "drag-test",
        component: DragTest,
      },
      {
        path: "views",
        name: "Views",
        component: () => import("@/pages/gotrading/Views.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "roles",
        name: "Roles",
        components: { default: Roles },
        meta: { title: "Roles" },
      },
      {
        path: "/gotrading/model-reference-mapping",
        name: "Model Reference Mapping",
        component: ModelReferenceMapping,
        beforeEnter: isAuthenticatedGuard,
      },
      {
        path: "/gotrading/admin-links",
        name: "Admin Links",
        component: AdminLinks,
        beforeEnter: isAuthenticatedGuard,
      },
      {
        path: "prompt-settings",
        name: "Prompt Settings",
        component: PromptSettings,
        beforeEnter: isAuthenticatedGuard,
      },
    ],
  },
  {
    path: "*",
    name: "NotFound",
    component: NotFound,
  },
];

export default routes;
